import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Injectable, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { debounceTime, filter, map, merge, mergeMap, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { fuseAnimations } from '@fuse/animations';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { Device, DevicePagination } from 'app/modules/admin/devicelist/devicelist.types';
import { DeviceListService, MSGraphDataStateWithTab } from 'app/modules/admin/devicelist/devicelist.service';
import { registerMgtComponents } from '@microsoft/mgt';
import { Html5QrcodeScanner } from "html5-qrcode";
import { FuseAlertType } from '@fuse/components/alert';
import { DeviceFieldMappings } from 'app/modules/admin/devicelist/devicelist.types';
import { DataStateChangeEvent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { AutoPilotRowCommand, NicheAppCollection } from '../../autopilotlist/autopilotlist.types';
import { openNicheAppsDialog } from '../../autopilotlist/grid/nicheapps.dialog';
import { MatDialog } from '@angular/material/dialog';


import { SelectEvent } from '@progress/kendo-angular-layout/tabstrip/events/select-event';
import { AutoPilotListService } from '../../autopilotlist/autopilotlist.service';

@Injectable()
export class MatPaginatorIntlCustomLabel extends MatPaginatorIntl {

  getRangeLabel = (page: number, pageSize: number, length: number) => {
    if (length === 0 || pageSize === 0) {
      return '0 of ' + length;
    }

    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex = startIndex < length ?
      Math.min(startIndex + pageSize, length) : startIndex + pageSize;
    return startIndex + 1 + ' - ' + endIndex;
  };
}


@Component({
    selector       : 'device-list',
    templateUrl    : './devicegrid.component.html',
    styles         : [
        /* language=SCSS */
        `

            .k-tabstrip-top > .k-content, .k-tabstrip-top > .k-tabstrip-content {
                padding: 0;
            }
            div.mat-mdc-form-field-infix {
                min-height: 32px !important;
            }

            .fuse-alert-container {
                padding: 8px !important;
            }

            .device-grid {
                grid-template-columns: 48px auto 40px;

                @screen sm {
                    grid-template-columns: 48px auto 112px 72px;
                }

                @screen md {
                    grid-template-columns: 200px 180px 100px 150px 160px 150px auto 60px;
                }

                @screen lg {
                    grid-template-columns: 200px 180px 100px 150px 160px 150px auto 60px;
                }
            }
            .device-image {
                display: inline-block;
                width: 24px;
                height: 24px;
                background-size: 24px 24px;
                background-position: center center;
                vertical-align: middle;
                line-height: 32px;
                margin-left: 5px;
            }

            .device-name {
                display: inline-block;
                vertical-align: middle;
                line-height: 32px;
                padding-left: 10px;
            }
            .k-button-solid-primary {
                -webkit-appearance: button;
                background-color: #3f51b5 !important;
                background-image: none;
            }
        `
    ],
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations     : fuseAnimations
})
export class DeviceGridComponent implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;


    deviceToImageMap = {
        "Microsoft Corporation": "assets/images/microsoft.png",
        "Dell Inc.": "assets/images/dell.png",
        "ASUSTeK COMPUTER INC.": "assets/images/hp.png",
        "VMware, Inc.": "assets/images/vmware.png",
        "HP": "assets/images/hp.png",
    };

    DeviceFieldMappings: DeviceFieldMappings = new DeviceFieldMappings();

    
    // TODO: obtain list of person from API

    public platformTitle: string;

    public griDataView: Observable<GridDataResult>;
    public state: MSGraphDataStateWithTab = { skip: 0, take: 500, search: '', persona: '', sort: [ { field: "deviceName", dir: "asc"} ], objectType: 'device', platform: 'windows' };

    devices$: Observable<Device[]>;
    personaList$: Observable<string[]>;

    flashMessage: 'success' | 'error' | null = null;
    isLoading: boolean = false;

    isDetailPaneOpen: boolean = false;
    isDetailLoaded: boolean = false;
    isDetailLoading: boolean = false;

    detailDevice: any | null = null;

    pagination: DevicePagination = {
        size: 10, length: 10, page: 1, startIndex: 1, lastPage: 1,
        endIndex: 1
    };
    searchInputControl: UntypedFormControl = new UntypedFormControl();
    personaControl: UntypedFormControl = new UntypedFormControl();
    rowCommands: AutoPilotRowCommand[];

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    nicheAppColls: NicheAppCollection[] = [];
    

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseConfirmationService: FuseConfirmationService,
        private _formBuilder: UntypedFormBuilder,
        private _devicelistService: DeviceListService,
        private _autopilotlistService: AutoPilotListService,
        private router: Router,
        private route: ActivatedRoute,
        private dialog: MatDialog
    )
    {
        this.griDataView = _devicelistService;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {

        // Subscribe to Router events
        const search = this.route.snapshot.queryParams['search'];
        if(search) {
            this.state.search = search;
            this.searchInputControl.setValue(search);
            this._changeDetectorRef.markForCheck();
        };

        this.platformTitle = this.route.snapshot.data['platformTitle'];

        this.state.platform = this.route.snapshot.data['platformFilter'];

        this.devices$ = this._devicelistService.devices$;

        this._devicelistService.loadRowCommands()
        .pipe(
            //map(commands => { 
            //    this.rowCommands = commands;
            //    return commands; 
            //}),
            mergeMap(commands => {
                this.rowCommands = commands;
                return this._autopilotlistService.getNicheAppCollections(this.state.platform);
            }),
            mergeMap(nicheAppColls => {

                this.nicheAppColls = nicheAppColls;

                return this._devicelistService.queryDeviceData(this.state);
            }),
            takeUntil(this._unsubscribeAll),

        ).subscribe();


        // Subscribe to search input field value changes
        merge(this.searchInputControl.valueChanges, this.personaControl.valueChanges)
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(500),
                switchMap(() => {
                    this.state.search = this.searchInputControl.value;
                    this.state.persona = this.personaControl.value;
                    // this.isLoading = true;
                    //return this._devicelistService.getDevices(this.pagination.size, this._sort.active, this._sort.direction, this.searchInputControl.value, this.personaControl.value);

                    return this._devicelistService.queryDeviceData(this.state);
                }),
            )
            .subscribe();

        this.personaList$ = this._devicelistService.queryPersonas();

        /*
        this.personaControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap((persona) => {
                    this.isLoading = true;
                    return this._devicelistService.getDevices(this.pagination.size, this._sort.active, this._sort.direction, this.searchInputControl.value, persona);
                }),
                map(() => {
                    this.isLoading = false;
                })
            )
            .subscribe();    
        */
       
        //this._devicelistService.getDevices()
        //    .pipe(takeUntil(this._unsubscribeAll))
        //    .subscribe();
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void
    {
        /*
        if ( this._sort && this._paginator )
        {
            // Set the initial sort
            this._sort.sort({
                id          : 'deviceName',
                start       : 'asc',
                disableClear: true
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;
                });


            // Get devices if sort or page changes
            this._sort.sortChange.pipe(
                switchMap(() => {
                    this.isLoading = true;
                    return this._devicelistService.getDevices(this._paginator.pageSize, this._sort.active, this._sort.direction, this.searchInputControl.value, this.personaControl.value);
                }),
                map(() => {
                    this.isLoading = false;
                })
            ).subscribe();

            this._paginator.page.pipe(
                switchMap((request) => {
                    this.isLoading = true;
                    // if(this._paginator.pageSize)
                    if (this._paginator.pageSize == this._devicelistService.currentSize && this._paginator.pageIndex > this._devicelistService.currentPage) {
                        return this._devicelistService.getNextDevices(this._paginator.pageSize, this._sort.active, this._sort.direction, this.searchInputControl.value, this.personaControl.value);
                    } else if (this._paginator.pageSize == this._devicelistService.currentSize && this._paginator.pageIndex < this._devicelistService.currentPage) {
                        return this._devicelistService.getPrevDevices(this._paginator.pageSize, this._sort.active, this._sort.direction, this.searchInputControl.value, this.personaControl.value);
                    } else  {
                        return this._devicelistService.getDevices(this._paginator.pageSize, this._sort.active, this._sort.direction, this.searchInputControl.value, this.personaControl.value);
                    }

                }),
                map(() => {
                    this.isLoading = false;
                })
            ).subscribe();
        }
        else
        {
            // alert('Error');
        }
        */
    }

    get isGridLoading(): boolean
    {
        return this._devicelistService.isLoading;
    }

    onPersonaSelectionChange($event)
    {

    } 

    public onUserExp(deviceId: string): void
    {
        this.router.navigate(['/user_experience', deviceId])
        // this._devicelistService.queryUserExperience(device.id).subscribe();
    }


    onAssignNicheApps(device: Device) {
        let counter = 0;
        let nadResult = null;

        this._autopilotlistService.getDeviceNicheApps(device.id).subscribe(nicheApps => {

            openNicheAppsDialog(this.dialog, device.deviceName, this.nicheAppColls, nicheApps).subscribe(result => { 

                if(!result)
                    return;

                nadResult = result;
                
                this._fuseConfirmationService.open({
                    title: 'Niche Apps',
                    message: 'Installation process will be start. Do you want to continue?',
                    icon: {
                        show: true,
                        name: "heroicons_outline:bell",
                        color: "warn"
                    },
                    actions: {
                        confirm: {
                            show: true,
                            label: "Yes",
                            color: "primary"
                        },
                        cancel: {
                            show: true
                        }
                    },
                    dismissible: false
                }).afterClosed().subscribe(result => {

                    if(result == 'confirmed') {

                        this._autopilotlistService.assignNicheApps(device.id, nadResult.currentAssignment).subscribe((updatedDevice) => {

                            this._fuseConfirmationService.open({
                                title: 'Done',
                                message: 'Installation initiated successfully.',
                                icon: {
                                    show: true,
                                    name: "heroicons_outline:annotation",
                                    color: "info"
                                },
                                actions: {
                                    confirm: {
                                        show: false,
                                        label: "Close",
                                        color: "primary"
                                    },
                                    cancel: {
                                        show: false
                                    }
                                },
                                dismissible: true
                            })
                        });
                    };
                });
            });
        });
        return true;
    }

    public onShowDetails(device: Device)
    {
        this.detailDevice = device;
        this.openDetailPane(true);
        this._changeDetectorRef.detectChanges();

        this._devicelistService.loadDeviceInfo(device.id).subscribe(response => {
            console.log('Davice info losded successfully');
            this.detailDevice = response;
            this.isDetailLoaded = true;
            this.isDetailLoading = false;
            this._changeDetectorRef.detectChanges();
          }, error => {
            console.log('Error loading Device Info', error);
            this.isDetailLoaded = false;
            this.isDetailLoading = false;
          });
    

        return true;
    }

    public openDetailPane(open: boolean = true)
    {
        this.isDetailPaneOpen = open;
    }
    

    public onPageChange(eventData: PageChangeEvent): void 
    {
        this.state.skip = eventData.skip;
        this._devicelistService.queryDeviceData(this.state);
    }

    public onDataStateChange(state: DataStateChangeEvent): void {
        this.state.sort = state.sort;
        // this.state.skip = 0;
        this._devicelistService.queryDeviceData(this.state);
    }


    onRowCommand(cmd: AutoPilotRowCommand, device: Device) {

        this._fuseConfirmationService.open({
            title: 'Confirm ' + cmd.displayName + ' action',
            confirmationCode: cmd.confirmationType == 'askDeviceName' ? device.deviceName : null,
            confirmationLabel: cmd.confirmationType == 'askDeviceName' ? "Enter the device name to proceed": null,
            message: cmd.confirmationMessage,
            icon: {
                show: true,
                name: "heroicons_outline:academic-cap",
                color: "info"
            },
            actions: {
                confirm: {
                    show: true,
                    label: "OK",
                    color: "primary"
                },
                cancel: {
                    show: true
                }
            },
            dismissible: true
        }).afterClosed().subscribe(result => {

            if(result == 'confirmed') {
                this._devicelistService.execCommand(device.id, cmd.id).subscribe((updatedDevice) => {

                    for(const [prop, value] of Object.entries(device))
                    {
                        if(updatedDevice[prop]) {
                            device[prop] = updatedDevice[prop];
                        }
                    }

                    device.labels = [{ "icon" : cmd.iconName, "label" : cmd.displayName }];

                    this._fuseConfirmationService.open({
                        title: 'Done',
                        message: 'Operation ' + cmd.displayName + ' on device "' + device.deviceName + '" has completed successfully',
                        icon: {
                            show: true,
                            name: "heroicons_outline:annotation",
                            color: "info"
                        },
                        actions: {
                            confirm: {
                                show: false,
                                label: "Close",
                                color: "primary"
                            },
                            cancel: {
                                show: false
                            }
                        },
                        dismissible: true
                    })

                    // Mark for check
                    this._changeDetectorRef.detectChanges();

                });
            }
        });
    }


    public onTabSelect(e: SelectEvent): void {
        switch(e.title) {
            case 'HEALTH':
                this.state.objectType = 'score';
                break;
            default:
            case 'DEVICES':
                this.state.objectType = 'device';
                break;
        }

        this._devicelistService.queryDeviceData(this.state);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    public getDeviceImage(manufacturer: string): string
    {
        if(this.deviceToImageMap[manufacturer])
        {
            return `url('${this.deviceToImageMap[manufacturer]}')`;
        } 
        else
        {
            return "url('assets/images/unknown-device.png')";
        }
    }
 
    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    /**
     * Formats a number to match desired format for the device score grid.
     * @param item the number to be formatted
     * @returns either "--" if item is -1, or the item expessed as an integer string.
     * i.e.,    item = -1: returns '--'
     *          item = 11: returns "11"
     *          item = 11.23: returns "11"
     */
    public formatScoreColumn(item: number): string
    {
        const removeDecimal = (item: number) => {
            const itemString:string = item + ''
            return itemString.split('.')[0];
        };

        if(item === -1) return '--';
        else return removeDecimal(item);        
    }
}
